import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faSpinner, faRepeat } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import EventFeatureFilterEditor, { buildEventFeatureFilter } from '@/components/admin/events/EventFeatureFilterEditor.tsx';
import ReminderMinutesEditor from '@/components/admin/events/ReminderMinutesEditor.tsx';
import { EventScopeSelector } from '@/components/admin/events/EventScopeDialog.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showErrorToast } from '@/utils/toasts.ts';
import { reminderMinutesToRows, reminderRowsToMinutes } from '@/utils/event-helpers';
import type { ReminderMinutesRow } from '@/utils/event-helpers';
import type { Event, EventSeriesScope, FeatureType } from '@/models';
import type { UpdateEventRequest } from '@/modelsAPI';

interface EditEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: Event;
  onEventUpdated: (event: Event, scope: EventSeriesScope) => void;
}

// Helper to convert ISO date string to date format (yyyy-MM-dd)
const toDateValue = (isoString: string): string => {
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to convert ISO date string to time format (HH:mm)
const toTimeValue = (isoString: string): string => {
  const date = new Date(isoString);
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

// Helper to compare arrays
const arraysEqual = <T,>(a: T[], b: T[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

export default function EditEventModal({ isOpen, onClose, event, onEventUpdated }: EditEventModalProps) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState('');
  const [endTime, setEndTime] = useState('');
  const [link, setLink] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [features, setFeatures] = useState<FeatureType[]>([]);
  const [excludeCommunityMembers, setExcludeCommunityMembers] = useState(false);
  const [onlyCommunityMembers, setOnlyCommunityMembers] = useState(false);
  const [reminderRows, setReminderRows] = useState<ReminderMinutesRow[]>([]);
  const [scope, setScope] = useState<EventSeriesScope>('SINGLE');
  const [loading, setLoading] = useState(false);

  const belongsToSeries = event.seriesID !== null;

  // FOLLOWING/ALL update many occurrences at once, so the backend rejects a body
  // that carries start/end - the times come from the series rule instead
  const timesEditable = scope === 'SINGLE';

  useEffect(() => {
    if (isOpen && event) {
      setSubject(event.subject);
      setDescription(event.description);
      setStartDate(toDateValue(event.start));
      setStartTime(toTimeValue(event.start));
      setEndDate(toDateValue(event.end));
      setEndTime(toTimeValue(event.end));
      setLink(event.link);
      setIsPublic(event.featureFilter === null);
      setFeatures(event.featureFilter?.features ?? []);
      setExcludeCommunityMembers(event.featureFilter?.excludeCommunityMembers ?? false);
      setOnlyCommunityMembers(event.featureFilter?.onlyCommunityMembers ?? false);
      setReminderRows(reminderMinutesToRows(event.reminderMinutes));
      setScope('SINGLE');
    }
  }, [isOpen, event]);

  // Switching to a series-wide scope discards any pending time edit, so put the
  // inputs back to the stored values to keep the form honest
  const handleScopeChange = (next: EventSeriesScope) => {
    setScope(next);
    if (next !== 'SINGLE') {
      setStartDate(toDateValue(event.start));
      setStartTime(toTimeValue(event.start));
      setEndDate(toDateValue(event.end));
      setEndTime(toTimeValue(event.end));
    }
  };

  const timesChanged = (): boolean =>
    startDate !== toDateValue(event.start) ||
    startTime !== toTimeValue(event.start) ||
    endDate !== toDateValue(event.end) ||
    endTime !== toTimeValue(event.end);

  const hasChanges = (): boolean => {
    if (!event) return false;
    const originalIsPublic = event.featureFilter === null;
    // Rows that don't parse are not a change - submitting reports the problem instead
    const reminderResult = reminderRowsToMinutes(reminderRows);
    return (
      subject !== event.subject ||
      description !== event.description ||
      (timesEditable && timesChanged()) ||
      link !== event.link ||
      isPublic !== originalIsPublic ||
      !arraysEqual(features, event.featureFilter?.features ?? []) ||
      excludeCommunityMembers !== (event.featureFilter?.excludeCommunityMembers ?? false) ||
      onlyCommunityMembers !== (event.featureFilter?.onlyCommunityMembers ?? false) ||
      ('minutes' in reminderResult && !arraysEqual(reminderResult.minutes, event.reminderMinutes ?? []))
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!subject.trim()) {
      showErrorToast('Bitte geben Sie einen Betreff ein');
      return;
    }

    if (!startDate || !startTime) {
      showErrorToast('Bitte geben Sie Startdatum und -zeit ein');
      return;
    }

    if (!endDate || !endTime) {
      showErrorToast('Bitte geben Sie Enddatum und -zeit ein');
      return;
    }

    if (!link.trim()) {
      showErrorToast('Bitte geben Sie einen Link ein');
      return;
    }

    if (!isPublic && features.length === 0) {
      showErrorToast('Bitte wählen Sie mindestens ein Feature aus oder markieren Sie das Event als öffentlich');
      return;
    }

    const startDateTime = new Date(`${startDate}T${startTime}`);
    const endDateTime = new Date(`${endDate}T${endTime}`);
    if (Number.isNaN(startDateTime.getTime()) || Number.isNaN(endDateTime.getTime())) {
      showErrorToast('Bitte geben Sie ein gültiges Datum und eine gültige Uhrzeit ein');
      return;
    }

    // Validate that end is after start
    if (endDateTime < startDateTime) {
      showErrorToast('Das Enddatum muss nach dem Startdatum liegen');
      return;
    }

    const reminderResult = reminderRowsToMinutes(reminderRows);
    if ('error' in reminderResult) {
      showErrorToast(reminderResult.error);
      return;
    }

    const request: UpdateEventRequest = {
      subject: subject.trim(),
      description: description.trim(),
      link: link.trim(),
      featureFilter: buildEventFeatureFilter(isPublic, features, excludeCommunityMembers, onlyCommunityMembers),
      reminderMinutes: reminderResult.minutes
    };

    // Only SINGLE may carry start/end - with FOLLOWING/ALL the backend answers 400
    if (timesEditable) {
      request.start = startDateTime.toISOString();
      request.end = endDateTime.toISOString();
    }

    setLoading(true);

    try {
      const updatedEvent = await APIRoutes.updateEvent(event.id, request, belongsToSeries ? scope : undefined);
      onEventUpdated(updatedEvent, scope);
      onClose();
    } catch (err) {
      handleAPIError(err, 'Fehler beim Aktualisieren des Events');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Event bearbeiten"
      icon={<FontAwesomeIcon icon={faEdit} className="w-5 h-5 text-blue-600" />}
      maxWidth="2xl"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6">
        <div className="space-y-4">
          {/* Scope - only relevant for events that belong to a series */}
          {belongsToSeries && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1 flex items-center">
                <FontAwesomeIcon icon={faRepeat} className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" />
                Dieser Termin gehört zu einer Serie
              </h3>
              <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                Welche Termine sollen geändert werden?
              </p>
              <EventScopeSelector scope={scope} onChange={handleScopeChange} disabled={loading} colorTheme="blue" />
              {event.seriesOverridden && scope === 'SINGLE' && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  Dieser Termin wurde bereits einzeln angepasst und wird von Änderungen an der Serie nicht mehr überschrieben.
                </p>
              )}
            </div>
          )}

          {/* Subject */}
          <div>
            <label htmlFor="subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Betreff *
            </label>
            <input
              type="text"
              id="subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="z.B. Live-Webinar: Marktanalyse"
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Beschreibung
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Optionale Beschreibung des Events..."
              disabled={loading}
            />
          </div>

          {/* Start/End Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Start */}
            <div>
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Start *
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  id="startDate"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !timesEditable}
                />
                <input
                  type="time"
                  id="startTime"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !timesEditable}
                />
              </div>
            </div>

            {/* End */}
            <div>
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Ende *
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  id="endDate"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !timesEditable}
                />
                <input
                  type="time"
                  id="endTime"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  className="w-28 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loading || !timesEditable}
                />
              </div>
            </div>
          </div>

          {!timesEditable && (
            <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
              Start und Ende lassen sich nur für einen einzelnen Termin ändern. Für die ganze Serie passen Sie den Rhythmus in der Serie selbst an.
            </p>
          )}

          {/* Link */}
          <div>
            <label htmlFor="link" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Link *
            </label>
            <input
              type="url"
              id="link"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="https://..."
              disabled={loading}
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Externer Link zum Event (z.B. Zoom, Teams, YouTube)
            </p>
          </div>

          <EventFeatureFilterEditor
            isPublic={isPublic}
            onIsPublicChange={setIsPublic}
            features={features}
            onFeaturesChange={setFeatures}
            excludeCommunityMembers={excludeCommunityMembers}
            onExcludeCommunityMembersChange={setExcludeCommunityMembers}
            onlyCommunityMembers={onlyCommunityMembers}
            onOnlyCommunityMembersChange={setOnlyCommunityMembers}
            disabled={loading}
            entityLabel="Event"
          />

          <ReminderMinutesEditor
            rows={reminderRows}
            onChange={setReminderRows}
            disabled={loading}
            remindersSent={event.remindersSent}
          />
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-200 dark:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 cursor-pointer"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || !subject.trim() || !startDate || !startTime || !endDate || !endTime || !link.trim() || (!isPublic && features.length === 0) || !hasChanges()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer flex items-center"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 mr-2 animate-spin" />
                Speichern...
              </>
            ) : (
              'Änderungen speichern'
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}
